import React, {
  useCallback,
  FC,
}                         from 'react';
import styled             from 'styled-components';
import {
  Form,
  Field,
  Formik,
  FormikProps,
  FormikConfig,
}                         from 'formik';
import * as Yup           from 'yup';
import { ICommentValues } from '@typings/comments';
import {
  Button,
  TextInput,
  FileUpload,
}                         from '@components/index';
import { EUploadTypes }   from '@enums/common';

const LineWrapper = styled.div`
  display       : flex;
  align-items   : center;
  width         : 800px;
  padding       : 5px;
  margin-bottom : 5px;

  > span:first-child {
    flex-basis  : 160px;
    font-size   : 16px;
    text-align  : right;
    white-space : nowrap;
    color       : ${({ theme: { textColor } }): string => textColor.black};
  }

  > div:last-child,
  > span:last-child {
    flex-basis  : 500px;
    margin-left : 15px;
  }

  > button {
    margin-left   : 15px;
    margin-bottom : 10px;
    span {
      font-size: 16px !important;
    }
  }

  > span:last-child {
    font-size : 16px;
    color     : ${({ theme: { textColor } }): string => textColor.darkGray};
  }
`;

const NewCommentLineWrapper = styled(LineWrapper)`
  align-items: flex-start;

  > span:first-child {
    margin-top: 10px;
  }
`;

const StyledInput = styled(TextInput)`
  && {
    background-color: ${({ theme: { backgroundColor } }): any => backgroundColor.white};

    textarea {
      font-size   : 16px;
      font-weight : 400;
      color       : ${({ theme: { textColor } }): string => textColor.gray};
    }
  }
`;

const initialValues: ICommentValues = {
  message: '',
};

const validationSchema: Yup.Schema<ICommentValues> = Yup.object().shape<ICommentValues>({
  message      : Yup.string().required(),
  documentFile : Yup.mixed(),
});

interface IProps {
  accountNumber?    : string;
  handleSaveComment : (comment: ICommentValues) => Promise<void>;
}

export const CommentForm: FC<IProps> = ({
  accountNumber,
  handleSaveComment,
}) => {
  const handleSubmit = useCallback<FormikConfig<ICommentValues>['onSubmit']>(
    async (values, { resetForm }) => {
      await handleSaveComment(values);
      resetForm();
    },
    [handleSaveComment],
  );

  return (
    <Formik
      onSubmit         = {handleSubmit}
      initialValues    = {initialValues}
      validationSchema = {validationSchema}
    >
      {({
        dirty,
        isValid,
        isSubmitting,
      }: FormikProps<ICommentValues>): JSX.Element => (
        <Form>
          {!!accountNumber && (
            <LineWrapper>
              <span>Account Number:</span>
              <span>{accountNumber}</span>
            </LineWrapper>
          )}

          <NewCommentLineWrapper>
            <span>New Comment:</span>
            <Field
              id        = "message"
              name      = "message"
              rows      = {10}
              rowsMax   = {15}
              component = {StyledInput}
              multiline
            />
          </NewCommentLineWrapper>

          <LineWrapper>
            <span>Document:</span>
            <FileUpload
              id       = "documentFile"
              name     = "documentFile"
              label    = "Choose File"
              color    = "default"
              fileType = {EUploadTypes.All}
            />
          </LineWrapper>

          <LineWrapper>
            <span />
            <Button
              width     = "180px"
              type      = "submit"
              label     = "Save Comments"
              isLoading = {isSubmitting}
              disabled  = {!isValid || !dirty || isSubmitting}
            />
          </LineWrapper>
        </Form>
      )}
    </Formik>
  );
};
